/**
 * ADR-0046 §5 (amended 2026-09-22): the person card.
 *
 * One card for a person, opened from their row in Users & Roles. Three panels
 * (Identity, Contact, Access), each with its owner chip; Previous / Next walk
 * the list as filtered; a shared address opens a chooser first.
 *
 * Editing is READ FIRST, EDIT PER PANEL (Ed, 2026-09-22):
 *  - Contact (PR 2a) edits in the card: the other panels dim, Previous/Next
 *    pause, and Save says out loud when it takes the details over from
 *    OneTouch — ADR-0046's one interactive moment.
 *  - Access (PR 2b) edits in the card too: the access editor renders inside
 *    the Access panel, keeping its ONE Save for production, environments and
 *    Circle of Support (Ed, 2026-09-18, reaffirmed 2026-09-22), with Suspend
 *    and End role set apart in the same edit mode.
 *  - Identity edits in the card as well (name, gender, birth date), with the
 *    same take-over wording as Contact. It used to open the old identity
 *    dialog, whose Cancel dropped the office back to the LIST (Ed, 2026-09-22).
 *    Photo: "Add / Change photo" under the avatar (PR 2c).
 *  - Buttons name what they edit; the owner chip sits by the panel heading.
 *
 * What the card shows and what Save sends are decided in person-card-view.ts
 * (pure, tested); this file only renders and wires.
 */
import { doc, DocumentReference, getDoc } from "firebase/firestore";

import { modalContainer } from "../dom";
import { db } from "../firebase";
import { go } from "../router";
import { escapeHtml } from "../utils/html";
import { callPractitionerRoleAction, callRelatedPersonAction } from "./cf-client";
import type { PersonProvenance } from "./loaders";
import {
  buildCardView, chooserOptions, clientDetail, photoOwners, contactEditTarget, currentContact, ukToday,
  needsChooser, neighbours, planContactEdit, planIdentityEdit, GENDERS,
  type AccessLine, type CardView, type ClientContextFacts, type ContactValues, type Owner,
  type PersonFacts, type IdentityValues,
} from "./person-card-view";
import { primaryNameParts } from "./person-card";
import type { CatalogueRow } from "./user-catalogue";

export interface PersonCardOptions {
  row: CatalogueRow;
  /** The list's keys in display order, as currently filtered. */
  keys: string[];
  rowByKey: (key: string) => CatalogueRow | undefined;
  /** Open the access editor for this row — embedded in the card's Access
   *  panel when `embed` is given (PR 2b), else as its own dialog. */
  onEditAccess: (
    row: CatalogueRow,
    embed?: { host: HTMLElement; onClose: (changed: boolean) => void },
  ) => void;
  /** A client card's circle links to each family member's own card. */
  rowForContact?: (relatedPersonDocId: string) => CatalogueRow | undefined;
  /** After a save in the card: refresh the list quietly and redraw this
   *  person's card in place (OMON-356). Resolves once the card is redrawn. */
  onSaved: (key: string) => void | Promise<void>;
}

function ownerChip(owner: Owner): string {
  if (!owner) return "";
  const title = owner === "OneTouch"
    ? "Kept up to date by the OneTouch sync"
    : "Maintained in SweetTree";
  return `<span class="scope-badge person-card-owner person-card-owner-${owner === "STC" ? "stc" : "onetouch"}" title="${title}">${owner}</span>`;
}

function accessRow(line: AccessLine): string {
  const value = line.inEffect
    ? `<span class="scope-badge scope-both">${escapeHtml(line.value)}</span>`
    : `<span class="person-card-muted">${escapeHtml(line.value)}</span>`;
  return `<div class="person-card-access-row"><span class="person-card-muted">${escapeHtml(line.label)}</span>${value}</div>`;
}

/**
 * A client's own identity and contact facts, from the SAME loader the client
 * page uses (ADR-0046 §6: read live, never copied into the catalogue — a
 * stale address is worse than a link). Imported lazily so the admin bundle
 * doesn't pull the client-page loaders for everyone else's card. A failure
 * leaves the card on what the list knows; it never blanks it.
 */
async function loadClientFacts(eocId: string): Promise<ClientContextFacts | null> {
  try {
    const [{ loadClientContext }, { state }] = await Promise.all([
      import("../loaders/client-context"),
      import("../state"),
    ]);
    const patient = state.patients.find((p) => p.docId === eocId);
    if (!patient) return null;
    return await loadClientContext(patient);
  } catch (err) {
    console.warn("person card: client facts failed", err);
    return null;
  }
}

async function loadFacts(personDocId: string | null): Promise<PersonFacts | null> {
  if (!personDocId) return null;
  try {
    const snap = await getDoc(doc(db, "_person", personDocId));
    if (!snap.exists()) return null;
    const data = snap.data() as Record<string, unknown>;
    // Staff: their `_practitioner`'s qualification list ("Soma trained").
    // One more read, and only for someone who has a carer record.
    const pracRef = ((data.link as Record<string, { reference?: unknown }> | undefined)
      ?._practitioner?.reference);
    let qualification: unknown[] | undefined;
    if (pracRef instanceof DocumentReference) {
      try {
        const prac = await getDoc(pracRef);
        if (prac.exists()) {
          const q = (prac.data() as Record<string, unknown>).qualification;
          qualification = Array.isArray(q) ? q : [];
        }
      } catch (err) {
        console.warn("person card: carer record unreadable", err);
      }
    }
    return {
      active: data.active !== false,
      telecom: data.telecom,
      address: data.address,
      provenance: (data._provenance ?? null) as PersonProvenance | null,
      photo: data.photo,
      name: data.name,
      gender: data.gender,
      birthDate: data.birthDate,
      qualification,
    };
  } catch (err) {
    // The card still shows everything the list already knows.
    console.error("person card: person record unreadable", err);
    return null;
  }
}

function input(id: string, label: string, value: string, type = "text"): string {
  return `
    <label class="edit-role-field" for="${id}">
      <span class="edit-role-label">${escapeHtml(label)}</span>
      <input type="${type}" id="${id}" value="${escapeHtml(value)}" autocomplete="off" spellcheck="false" />
    </label>`;
}

/** The Contact panel in edit mode. The warning is the take-over moment:
 *  saving makes SweetTree the owner, and OneTouch stops changing it. */
function contactForm(row: CatalogueRow, view: CardView, values: ContactValues): string {
  const target = contactEditTarget(row);
  const who = escapeHtml(view.name);
  const warning = target === "family"
    ? `<strong>Saving makes SweetTree the owner of ${who}’s contact details</strong> for every client they’re family of. OneTouch will stop changing them.`
    : view.contactOwner === "STC"
      ? ""
      : `<strong>OneTouch keeps these up to date today.</strong> Saving makes SweetTree the owner of ${who}’s contact details: OneTouch will stop changing them, and any difference will be flagged instead.`;
  const address = target === "staff"
    ? input("pcAddrLine", "Address", values.line) + input("pcAddrCity", "Town", values.city)
      + input("pcAddrPostcode", "Postcode", values.postalCode)
    : `<div class="person-card-muted person-card-note">A family member’s address is kept in OneTouch.</div>`;
  const saveLabel = warning ? "Take over and save" : "Save contact";
  return `
    <div class="person-card-editing">
      <div class="person-card-panel-head"><span class="person-card-label">Editing contact</span>${ownerChip(view.contactOwner)}</div>
      ${input("pcEmail", "Email", values.email, "email")}
      ${input("pcMobile", "Mobile", values.mobile, "tel")}
      ${address}
      ${warning ? `<div class="person-card-warning">${warning}</div>` : ""}
      <div class="edit-role-status" id="pcContactStatus" aria-live="polite"></div>
      <div class="person-card-edit-actions">
        <button class="btn-outline" id="pcContactCancel">Cancel</button>
        <button class="btn-primary" id="pcContactSave">${saveLabel}</button>
      </div>
    </div>`;
}

/** The Identity panel in edit mode — the same shape as Contact's: the
 *  take-over is said out loud before Save when OneTouch keeps it today. */
function identityForm(view: CardView, values: IdentityValues): string {
  const who = escapeHtml(view.name);
  const warning = view.identityOwner === "STC"
    ? ""
    : `<strong>OneTouch keeps these up to date today.</strong> Saving makes SweetTree the owner of ${who}’s name, gender and birth date: OneTouch will stop changing them, and any difference will be flagged instead.`;
  const genderOptions = GENDERS
    .map((g) => `<option value="${g}"${g === values.gender ? " selected" : ""}>${g || "—"}</option>`)
    .join("");
  return `
    <div class="person-card-editing">
      <div class="person-card-panel-head"><span class="person-card-label">Editing identity</span>${ownerChip(view.identityOwner)}</div>
      ${input("pcGiven", "First name", values.given)}
      ${input("pcFamily", "Family name", values.family)}
      <label class="edit-role-field" for="pcGender">
        <span class="edit-role-label">Gender</span>
        <select id="pcGender">${genderOptions}</select>
      </label>
      ${input("pcBirthDate", "Birth date", values.birthDate, "date")}
      ${warning ? `<div class="person-card-warning">${warning}</div>` : ""}
      <div class="edit-role-status" id="pcIdentityStatus" aria-live="polite"></div>
      <div class="person-card-edit-actions">
        <button class="btn-outline" id="pcIdentityCancel">Cancel</button>
        <button class="btn-primary" id="pcIdentitySave">${warning ? "Take over and save" : "Save identity"}</button>
      </div>
    </div>`;
}

function renderCard(
  view: CardView, opts: PersonCardOptions, editing: ContactValues | null, accessEditing = false,
  identityEditing: IdentityValues | null = null,
  clientFacts: ClientContextFacts | null = null,
): string {
  const { row } = opts;
  const nav = neighbours(opts.keys, row.key);
  // One panel is edited at a time; the others step back (Ed, 2026-09-22).
  const busy = editing !== null || accessEditing || identityEditing !== null;
  const dimIdentity = editing !== null || accessEditing ? " person-card-dimmed" : "";
  const dimContact = accessEditing || identityEditing ? " person-card-dimmed" : "";
  const dimAccess = editing || identityEditing ? " person-card-dimmed" : "";
  const why = view.why.map((w) => `<span class="person-card-why">${escapeHtml(w)}</span>`).join("");
  const employment = view.employment
    ? `<div class="person-card-box">
         <div class="person-card-box-head"><span class="person-card-label">Employment</span>${ownerChip(view.employment.owner)}</div>
         <div class="person-card-status"><span class="person-card-dot ${view.employment.active ? "is-on" : ""}"></span>
           <strong>${view.employment.active ? "Active" : "Inactive"}</strong></div>
       </div>`
    : "";
  // "Soma trained" (Ed, 2026-09-23/24): the date from which this carer owes
  // in-app shift records. Read here; edited in place (wireSoma).
  const soma = view.soma
    ? `<div class="person-card-box" id="pcSoma">
         <div class="person-card-box-head"><span class="person-card-label">Soma trained</span>
           ${busy ? "" : `<button class="btn-outline person-card-edit" id="pcSomaEdit">${
             view.soma.start ? "Change" : "Record"}</button>`}</div>
         <div>${escapeHtml(view.soma.label)}</div>
         <div class="person-card-muted person-card-note">Owes in-app shift records from this date.</div>
       </div>`
    : "";
  const contactRead = view.contact.map((f) => `
      <div class="person-card-field"><div class="person-card-muted">${escapeHtml(f.label)}</div><div>${escapeHtml(f.value)}</div></div>`).join("");
  // Buttons say WHAT they edit; the owner chip beside the heading says who
  // keeps it (Ed, 2026-09-22: two "STC · Edit" pairs read as the same thing).
  const contactEdit = contactEditTarget(row) && !busy
    ? `<button class="btn-outline person-card-edit" id="personCardEditContact">Edit contact</button>` : "";
  const contact = editing
    ? contactForm(row, view, editing)
    : `<div class="person-card-panel-head"><span class="person-card-label">Contact</span>${ownerChip(view.contactOwner)}${contactEdit}</div>
       ${contactRead}`;
  const access = [...view.production, ...view.environments].map(accessRow).join("")
    || `<div class="person-card-muted">No production or environment access.</div>`;
  const family = view.family.length === 0
    ? `<div>Not a family contact of any client.</div>`
    : view.family.map((f) => {
        const client = f.eocId
          ? `<a href="#" class="person-card-client-link" data-eoc="${escapeHtml(f.eocId)}">${escapeHtml(f.client)} →</a>`
          : escapeHtml(f.client);
        const state = f.inEffect
          ? `<span class="scope-badge scope-both">${escapeHtml(f.state)}</span>`
          : `<span class="person-card-muted">${escapeHtml(f.state)}</span>`;
        return `<div class="person-card-access-row"><span>${client} <span class="person-card-muted">· ${escapeHtml(f.relationship)}</span></span>${state}</div>`;
      }).join("");
  const identityEdit = row.personDocId && !busy
    ? `<button class="btn-outline person-card-edit" id="personCardEditIdentity">Edit identity</button>` : "";
  const accessEdit = busy ? "" : `<button class="btn-outline person-card-edit" id="personCardEditAccess">Edit access</button>`;
  // ADR-0046 §6: a CLIENT's card. Listed here, never edited here — their
  // care and details live on the client page, one click away.
  const c = view.client;
  const clientLink = c
    ? `<a href="#" class="person-card-client-link" data-eoc="${escapeHtml(c.eocId)}">Client card →</a>` : "";
  const circle = c && (c.circle.length === 0
    ? `<div class="person-card-muted">No family on file. Family contacts come from OneTouch.</div>`
    : c.circle.map((l) => {
        const state = l.inEffect
          ? `<span class="scope-badge scope-both">${escapeHtml(l.state)}</span>`
          : `<span class="person-card-muted">${escapeHtml(l.state)}</span>`;
        return `<div class="person-card-access-row"><span><a href="#" class="person-card-client-link" data-contact="${escapeHtml(l.relatedPersonDocId)}">${escapeHtml(l.name)} →</a>
          <span class="person-card-muted">· ${escapeHtml(l.relationship)}</span></span>${state}</div>`;
      }).join(""));
  // A client's own details, read live from their records (never copied) and
  // filled in by hydrateClient() once they arrive.
  const cd = c ? clientDetail(c, clientFacts) : null;
  const field = (label: string, value: string) =>
    `<div><div class="person-card-muted person-card-note">${escapeHtml(label)}</div>
      <div>${escapeHtml(value)}</div></div>`;
  const contactBody = c && cd
    ? `<div class="person-card-panel-head">
         <span class="person-card-label">Contact</span>${ownerChip(view.contactOwner)}
       </div>
       <div class="person-card-box" id="pcClientContact">
         ${field("Address", cd.address)}
         ${field("Phone", cd.phone)}
       </div>
       <div class="person-card-box" id="pcClientPlacement">
         ${field("Area", cd.area)}
         ${field("Care manager", cd.careManager)}
       </div>
       <div class="person-card-muted person-card-note">A client’s details are edited on their client page.</div>`
    : contact;
  const accessBody = c && cd
    ? `<div class="person-card-panel-head"><span class="person-card-label">Care</span></div>
       <div class="person-card-box">
         <div class="person-card-access-row">
           <span>${escapeHtml(cd.plan)}</span>
           <a href="#" class="person-card-client-link" data-eoc="${escapeHtml(c.eocId)}">Open client page →</a>
         </div>
         ${cd.planNote ? `<div class="person-card-muted person-card-note">${escapeHtml(cd.planNote)}</div>` : ""}
       </div>
       <div class="person-card-panel-head"><span class="person-card-label">Circle of Support</span></div>
       <div class="person-card-box">${circle}</div>
       <div class="person-card-muted person-card-note">Who can see this client in the Circle of Support app. Open a family member to give or remove access.</div>`
    : accessEditing
    ? `<div id="pcAccessHost"></div>`
    : `<div class="person-card-panel-head">
         <span class="person-card-label">Access</span>${ownerChip("STC")}
         ${accessEdit}
       </div>
       <div class="person-card-box">${access}</div>
       <div class="person-card-box"><div class="person-card-muted">Circle of Support</div>${family}</div>
       <div class="person-card-box"><div class="person-card-muted">Signs in with</div><div>${escapeHtml(view.signIn)}</div></div>`;

  return `
    <div class="modal-overlay">
      <div class="modal-box person-card-modal modal-pinned" role="dialog" aria-labelledby="personCardName">
        <div class="person-card-head">
          <span class="person-card-label">Person</span>
          <div class="person-card-nav">
            <button class="btn-outline" id="personCardPrev"${nav.prev && !busy ? "" : " disabled"}>← Previous</button>
            <span class="person-card-muted">${busy ? "Finish editing to move on" : nav.total > 0 ? `${nav.index} of ${nav.total}` : ""}</span>
            <button class="btn-outline" id="personCardNext"${nav.next && !busy ? "" : " disabled"}>Next →</button>
          </div>
        </div>

        <div class="modal-scroll">
          <div class="person-card-grid">
            <section class="person-card-identity${dimIdentity}" aria-label="Identity">
              ${identityEditing ? `<h3 id="personCardName">${escapeHtml(view.name)}</h3>${identityForm(view, identityEditing)}` : `
              <div class="person-card-panel-head person-card-identity-head">
                <span class="person-card-label">Identity</span>${ownerChip(view.identityOwner)}
                ${identityEdit}
              </div>
              <div class="person-card-avatar" aria-hidden="true"${
                // A client's photo lives on their own record (MONO-106), so it
                // arrives with their facts, not with the catalogue row.
                (cd?.photoPath ?? view.photoPath)
                  ? ` data-avatar-path="${escapeHtml(cd?.photoPath ?? view.photoPath!)}"` : ""
              }>${escapeHtml(view.initials)}</div>
              ${!busy && photoOwners(row).length > 0
                ? `<button class="btn-outline person-card-edit" id="personCardPhoto">${view.photoPath ? "Change photo" : "Add photo"}</button>
                   <div class="edit-role-status" id="personCardPhotoStatus" aria-live="polite"></div>`
                : ""}
              <h3 id="personCardName">${escapeHtml(view.name)}</h3>
              <div class="person-card-whys">${why}</div>
              ${employment}
              ${soma}
              ${cd
                ? `<div class="person-card-muted person-card-note" id="pcClientBorn">${escapeHtml(cd.born)}</div>
                   <div class="person-card-status">
                     <span class="person-card-dot${cd.active ? " is-on" : ""}"></span>
                     <span>${escapeHtml(cd.status)}</span>
                   </div>
                   <a href="#" class="person-card-client-link" data-eoc="${escapeHtml(c!.eocId)}">Open client page →</a>`
                : clientLink}`}
            </section>

            <section class="${dimContact.trim()}" aria-label="Contact">
              ${contactBody}
            </section>

            <section class="${dimAccess.trim()}" aria-label="Access">
              ${accessBody}
            </section>
          </div>
        </div>

        <div class="modal-actions">
          <button class="btn-outline" id="personCardClose">Close</button>
        </div>
      </div>
    </div>`;
}

function renderChooser(row: CatalogueRow): string {
  const options = chooserOptions(row).map((o, i) => `
      <button class="person-card-choice" data-choice="${i}">
        <span><strong>${escapeHtml(o.name)}</strong><br><span class="person-card-muted">${escapeHtml(o.detail)}</span></span>
        <span aria-hidden="true">→</span>
      </button>`).join("");
  return `
    <div class="modal-overlay">
      <div class="modal-box edit-role-modal" role="dialog" aria-labelledby="personChooserTitle">
        <span class="person-card-label">Who do you mean?</span>
        <h3 id="personChooserTitle">${escapeHtml(row.email ?? "")}</h3>
        <div class="edit-role-status edit-role-status-error">⚠ ${chooserOptions(row).length} people share this address. Whoever reads it can sign in as any of them.</div>
        <div class="person-card-choices">${options}</div>
        <div class="modal-actions"><button class="btn-outline" id="personChooserClose">Close</button></div>
      </div>
    </div>`;
}

let _escHandler: ((e: KeyboardEvent) => void) | null = null;

function close(): void {
  modalContainer.innerHTML = "";
  if (_escHandler) document.removeEventListener("keydown", _escHandler);
  _escHandler = null;
}

/** Close the card from outside (e.g. the row it showed is gone after a save). */
export function closePersonCard(): void {
  close();
}

/**
 * OMON-356: what every panel Save does once the server has accepted it.
 *
 * It used to `close()` the card and let the list reload — the list blanked to
 * "Loading…" (every person, clients included) and the card came back only
 * afterwards, in read mode: Ed's "the screen blanks out, then eventually comes
 * back". Now the card STAYS on screen, says it is updating, and the caller
 * redraws it in place once the quiet list refresh lands. Where you land is
 * the card's own rule — read first, edit one panel at a time — the same place
 * Cancel takes you, now showing what was saved.
 */
async function afterSave(opts: PersonCardOptions): Promise<void> {
  const card = modalContainer.querySelector<HTMLElement>(".person-card-modal");
  card?.setAttribute("aria-busy", "true");
  card?.querySelectorAll<HTMLButtonElement>("button").forEach((b) => { b.disabled = true; });
  // The header's status spot ("Finish editing to move on") says what is happening.
  const note = card?.querySelector<HTMLElement>(".person-card-head .person-card-muted");
  if (note) {
    note.setAttribute("role", "status");
    note.textContent = "Saved — updating…";
  }
  try {
    await opts.onSaved(opts.row.key);
  } catch (err) {
    console.error("person card refresh after save failed:", err);
    close();
  }
}

function wireCommon(closeId: string): void {
  if (_escHandler) document.removeEventListener("keydown", _escHandler);
  _escHandler = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
  document.addEventListener("keydown", _escHandler);
  modalContainer.querySelector(".modal-overlay")?.addEventListener("click", (e) => {
    if ((e.target as HTMLElement).classList.contains("modal-overlay")) close();
  });
  modalContainer.querySelector(`#${closeId}`)?.addEventListener("click", close);
}

/** Open the card for a row — the chooser first when the address is shared. */
export async function openPersonCard(opts: PersonCardOptions): Promise<void> {
  const { row } = opts;
  if (needsChooser(row)) {
    const options = chooserOptions(row);
    modalContainer.innerHTML = renderChooser(row);
    wireCommon("personChooserClose");
    modalContainer.querySelectorAll<HTMLButtonElement>("[data-choice]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const pick = options[Number(btn.dataset.choice)];
        if (pick) void showCard({ ...opts, row: pick.row });
      });
    });
    return;
  }
  await showCard(opts);
}

function currentIdentity(facts: PersonFacts | null): IdentityValues {
  const { given, family } = primaryNameParts(facts?.name);
  const gender = typeof facts?.gender === "string" ? facts.gender : "";
  const birthDate = typeof facts?.birthDate === "string" ? facts.birthDate.slice(0, 10) : "";
  return { given, family, gender, birthDate };
}

async function showCard(
  opts: PersonCardOptions, facts?: PersonFacts | null, editing = false, accessEditing = false,
  identityEditing = false, clientFacts: ClientContextFacts | null = null,
): Promise<void> {
  const known = facts === undefined ? await loadFacts(opts.row.personDocId) : facts;
  const view = buildCardView(opts.row, known);
  const before = currentContact(opts.row, known);
  const identityBefore = currentIdentity(known);
  modalContainer.innerHTML = renderCard(
    view, opts, editing ? before : null, accessEditing, identityEditing ? identityBefore : null,
    clientFacts);
  wireCommon("personCardClose");
  const q = <T extends HTMLElement>(sel: string) => modalContainer.querySelector<T>(sel);

  if (identityEditing) {
    // Cancel returns to the CARD, never past it to the list (Ed's bug,
    // 2026-09-22: the old dialog closed the card before it opened).
    q("#pcIdentityCancel")?.addEventListener("click", () => void showCard(opts, known));
    q<HTMLInputElement>("#pcGiven")?.focus();
    q("#pcIdentitySave")?.addEventListener("click", () => void saveIdentity(opts, view, known, identityBefore));
    return;
  }

  if (accessEditing) {
    // PR 2b: the access editor lives in the Access panel, with its ONE Save.
    // Closing it refreshes the card if anything was saved, else just returns
    // to reading.
    const host = q("#pcAccessHost");
    if (host) {
      opts.onEditAccess(opts.row, {
        host,
        onClose: (changed) => {
          if (changed) {
            void afterSave(opts);
          } else {
            void showCard(opts, known);
          }
        },
      });
    }
    return;
  }

  if (editing) {
    q("#pcContactCancel")?.addEventListener("click", () => void showCard(opts, known, false));
    q<HTMLInputElement>("#pcEmail")?.focus();
    q("#pcContactSave")?.addEventListener("click", () => void saveContact(opts, view, known, before));
    return;
  }

  const nav = neighbours(opts.keys, opts.row.key);
  const step = (key: string | null) => {
    const next = key ? opts.rowByKey(key) : undefined;
    if (next) void openPersonCard({ ...opts, row: next });
  };
  q("#personCardPrev")?.addEventListener("click", () => step(nav.prev));
  q("#personCardNext")?.addEventListener("click", () => step(nav.next));
  q("#personCardEditContact")?.addEventListener("click", () => void showCard(opts, known, true));
  q("#personCardPhoto")?.addEventListener("click", () => pickAndUploadPhoto(opts));
  hydratePhoto();
  wireSoma(opts, view, known);
  // A client's own details arrive a moment after the card: the card opens on
  // what the list already knows, then fills in. Re-rendering with the facts
  // keeps ONE render path, so a client card can never drift from the mock.
  if (view.client && clientFacts === null) {
    void loadClientFacts(view.client.eocId).then((got) => {
      if (got && modalContainer.querySelector("#pcClientContact")) {
        void showCard(opts, known, false, false, false, got);
      }
    });
  }
  q("#personCardEditAccess")?.addEventListener("click", () => void showCard(opts, known, false, true));
  q("#personCardEditIdentity")?.addEventListener("click", () => void showCard(opts, known, false, false, true));
  // Link out to the client, never copy their care details in (ADR-0046 §5).
  modalContainer.querySelectorAll<HTMLAnchorElement>("[data-eoc]").forEach((a) => {
    a.addEventListener("click", (e) => {
      e.preventDefault();
      const eocId = a.dataset.eoc;
      close();
      if (eocId) go({ kind: "client", eocId, tab: "developmental" });
    });
  });
  // A client's circle: each family member opens their OWN card, where their
  // access is given or removed.
  modalContainer.querySelectorAll<HTMLAnchorElement>("[data-contact]").forEach((a) => {
    a.addEventListener("click", (e) => {
      e.preventDefault();
      const person = a.dataset.contact ? opts.rowForContact?.(a.dataset.contact) : undefined;
      if (person) void openPersonCard({ ...opts, row: person });
    });
  });
}

async function saveContact(
  opts: PersonCardOptions, view: CardView, facts: PersonFacts | null, before: ContactValues,
): Promise<void> {
  const val = (id: string) => modalContainer.querySelector<HTMLInputElement>(`#${id}`)?.value ?? "";
  const form: ContactValues = {
    email: val("pcEmail"), mobile: val("pcMobile"),
    line: val("pcAddrLine"), city: val("pcAddrCity"), postalCode: val("pcAddrPostcode"),
  };
  // Family rows have no address inputs: keep the address exactly as it was.
  if (!modalContainer.querySelector("#pcAddrLine")) {
    form.line = before.line; form.city = before.city; form.postalCode = before.postalCode;
  }
  const status = modalContainer.querySelector<HTMLElement>("#pcContactStatus")!;
  const saveBtn = modalContainer.querySelector<HTMLButtonElement>("#pcContactSave")!;
  const plan = planContactEdit(opts.row, before, form);
  if (plan.kind === "invalid") {
    status.textContent = plan.message;
    status.classList.add("edit-role-status-error");
    return;
  }
  if (plan.kind === "none") {
    void showCard(opts, facts, false);
    return;
  }

  saveBtn.disabled = true;
  status.classList.remove("edit-role-status-error");
  status.textContent = "Saving…";
  try {
    if (plan.kind === "staff") {
      await callPractitionerRoleAction({
        action: "update-person",
        personDocId: opts.row.personDocId!,
        fields: plan.fields,
        // The warning above WAS the confirm: take over unless STC already owns it.
        ...(view.contactOwner === "STC" ? {} : { takeOverDomains: ["contact"] }),
      });
    } else {
      if (plan.email) {
        await callRelatedPersonAction({
          action: "set-email", relatedPersonDocIds: plan.relatedPersonDocIds, email: plan.email,
        });
      }
      if (plan.mobile) {
        for (const id of plan.relatedPersonDocIds) {
          await callRelatedPersonAction({ action: "attach-number", relatedPersonDocId: id, mobile: plan.mobile });
        }
      }
    }
    void afterSave(opts);
  } catch (err) {
    saveBtn.disabled = false;
    status.textContent = err instanceof Error ? err.message : "Saving failed.";
    status.classList.add("edit-role-status-error");
  }
}

async function saveIdentity(
  opts: PersonCardOptions, view: CardView, facts: PersonFacts | null, before: IdentityValues,
): Promise<void> {
  const val = (id: string) =>
    modalContainer.querySelector<HTMLInputElement | HTMLSelectElement>(`#${id}`)?.value ?? "";
  const status = modalContainer.querySelector<HTMLElement>("#pcIdentityStatus")!;
  const saveBtn = modalContainer.querySelector<HTMLButtonElement>("#pcIdentitySave")!;
  const plan = planIdentityEdit(before, {
    given: val("pcGiven"), family: val("pcFamily"), gender: val("pcGender"), birthDate: val("pcBirthDate"),
  });
  if (plan.kind === "invalid") {
    status.textContent = plan.message;
    status.classList.add("edit-role-status-error");
    return;
  }
  if (plan.kind === "none" || !opts.row.personDocId) {
    void showCard(opts, facts);
    return;
  }
  saveBtn.disabled = true;
  status.classList.remove("edit-role-status-error");
  status.textContent = "Saving…";
  try {
    await callPractitionerRoleAction({
      action: "update-person",
      personDocId: opts.row.personDocId,
      fields: plan.fields,
      // The warning above WAS the confirm (ADR-0046 §5): take over unless
      // SweetTree already owns identity.
      ...(view.identityOwner === "STC" ? {} : { takeOverDomains: ["identity"] }),
    });
    void afterSave(opts);
  } catch (err) {
    saveBtn.disabled = false;
    status.textContent = err instanceof Error ? err.message : "Saving failed.";
    status.classList.add("edit-role-status-error");
  }
}

// ── "Soma trained": record, change or remove, in place ──────────────────

/**
 * The Soma box turns into a small form: a date (today by default, never in
 * the future), Save, Remove when there is one, Cancel. Admin only — the
 * whole card is, and the server checks again. Saving reloads the card from
 * the record, so what it then shows is what was written.
 */
function wireSoma(opts: PersonCardOptions, view: CardView, facts: PersonFacts | null): void {
  const box = modalContainer.querySelector<HTMLElement>("#pcSoma");
  const edit = modalContainer.querySelector<HTMLButtonElement>("#pcSomaEdit");
  const personDocId = opts.row.personDocId;
  if (!box || !edit || !view.soma || !personDocId) return;
  const current = view.soma.start;
  edit.addEventListener("click", () => {
    const today = ukToday();
    box.innerHTML = `
      <div class="person-card-box-head"><span class="person-card-label">Soma trained</span></div>
      <label class="person-card-muted" for="pcSomaDate">Date trained</label>
      <input type="date" id="pcSomaDate" value="${escapeHtml(current ?? today)}" max="${today}">
      <div class="person-card-muted person-card-note">Owes in-app shift records from this date.</div>
      <div class="edit-role-status" id="pcSomaStatus" aria-live="polite"></div>
      <div class="person-card-panel-foot">
        ${current ? `<button class="btn-outline" id="pcSomaRemove">Remove</button>` : ""}
        <button class="btn-outline" id="pcSomaCancel">Cancel</button>
        <button class="btn-primary" id="pcSomaSave">Save</button>
      </div>`;
    const q = <T extends HTMLElement>(sel: string) => box.querySelector<T>(sel);
    q<HTMLInputElement>("#pcSomaDate")?.focus();
    q("#pcSomaCancel")?.addEventListener("click", () => void showCard(opts, facts));
    const status = q<HTMLElement>("#pcSomaStatus")!;
    const send = async (start: string | null) => {
      box.querySelectorAll<HTMLButtonElement>("button").forEach((b) => { b.disabled = true; });
      status.classList.remove("edit-role-status-error");
      status.textContent = "Saving…";
      try {
        await callPractitionerRoleAction({
          action: "set-qualification", personDocId,
          qualification: "soma-trained", start,
        });
        void showCard(opts);   // re-read, so the card shows what was written
      } catch (err) {
        box.querySelectorAll<HTMLButtonElement>("button").forEach((b) => { b.disabled = false; });
        status.textContent = err instanceof Error ? err.message : "Saving failed.";
        status.classList.add("edit-role-status-error");
      }
    };
    q("#pcSomaSave")?.addEventListener("click", () => {
      const value = q<HTMLInputElement>("#pcSomaDate")?.value ?? "";
      if (!value || value > today) {
        status.textContent = value ? "The training date can't be in the future." : "Choose a date.";
        status.classList.add("edit-role-status-error");
        return;
      }
      void send(value);
    });
    q("#pcSomaRemove")?.addEventListener("click", () => void send(null));
  });
}

// ── PR 2c: the photo — the client card's ADR-0031 pipeline, reused ──────

/** Swap the initials for the photo once it has loaded (rules-gated Storage
 *  read; staff may read media/**). On any failure the initials simply stay. */
function hydratePhoto(): void {
  const el = modalContainer.querySelector<HTMLElement>(".person-card-avatar[data-avatar-path]");
  const path = el?.getAttribute("data-avatar-path");
  if (!el || !path) return;
  void (async () => {
    try {
      const { getBlob, ref } = await import("firebase/storage");
      const { storage } = await import("../firebase");
      const url = URL.createObjectURL(await getBlob(ref(storage, path)));
      el.innerHTML = `<img src="${url}" alt="" class="person-card-photo">`;
    } catch (err) {
      console.warn("person card: photo load failed", err);
    }
  })();
}

/**
 * Pick an image and send it through the ADR-0031 quarantine, exactly as the
 * client card does: `uploads/{uid}/{uuid}` with the OWNER in its metadata;
 * agent.media validates it, strips EXIF (including location), makes the
 * thumbnails and writes `photo[0]` with a paired audit event. A family member
 * gets the same photo on every contact record of theirs.
 */
function pickAndUploadPhoto(opts: PersonCardOptions): void {
  const owners = photoOwners(opts.row);
  const status = modalContainer.querySelector<HTMLElement>("#personCardPhotoStatus");
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/jpeg,image/png,image/webp";
  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file || owners.length === 0) return;
    const say = (text: string, error = false) => {
      if (!status) return;
      status.textContent = text;
      status.classList.toggle("edit-role-status-error", error);
    };
    try {
      const { ref, uploadBytes } = await import("firebase/storage");
      const { getDoc, doc: fsDoc } = await import("firebase/firestore");
      const { storage, auth } = await import("../firebase");
      const uid = auth.currentUser?.uid;
      if (!uid) return;
      // What the first owner holds now, so we can tell when the new photo
      // lands. ☠️ A refused or failed READ must never turn a successful
      // upload into an error message: `undefined` means "can't tell".
      const photoUrl = async (o: typeof owners[number]): Promise<string | null | undefined> => {
        try {
          const snap = await getDoc(fsDoc(db, o.collection, o.docId));
          const photo = (snap.data() as { photo?: Array<{ url?: string }> } | undefined)?.photo;
          return photo?.[0]?.url ?? null;
        } catch {
          return undefined;
        }
      };
      const before = await photoUrl(owners[0]);
      say("Uploading…");
      for (const o of owners) {
        await uploadBytes(ref(storage, `uploads/${uid}/${crypto.randomUUID()}`), file, {
          contentType: file.type,
          customMetadata: { ownerCollection: o.collection, ownerDocId: o.docId },
        });
      }
      // agent.media usually lands it within seconds; wait up to ~15s.
      say("Processing…");
      for (let i = 0; before !== undefined && i < 10; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        const now = await photoUrl(owners[0]);
        if (now !== undefined && now !== before) {
          void afterSave(opts);
          return;
        }
      }
      say("Uploaded. The photo will appear shortly — reopen the card in a moment.");
    } catch (err) {
      console.warn("person card: photo upload failed", err);
      say("The photo couldn't be uploaded. It must be a JPEG, PNG or WebP image.", true);
    }
  };
  input.click();
}
